import { Component, Input, OnInit } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { concatMap, of } from 'rxjs';

import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { TextAreaFieldComponent } from 'src/app/components/form/textarea-field/textarea-field.component';
import { ProjectsFinanceAttachmentComponent } from '../projects-finance-attachment/projects-finance-attachment.component';
import { ProjectFinanceService } from 'src/app/services/api/project-finance.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { RouteService } from 'src/app/services/route.service';
import {
  PROJECT_FINANCE_DEFAULT_CURRENCY,
  ProjectInvoice,
  ProjectInvoiceUpdatePayload,
  projectInvoiceStatusClass,
  projectInvoiceStatusLabel
} from 'src/app/models/entities/project-finance';

@Component({
  selector: 'projects-finance-instalment-form',
  imports: [
    ReactiveFormsModule,
    CurrencyPipe,
    LoaderComponent,
    InputFieldComponent,
    TextAreaFieldComponent,
    ProjectsFinanceAttachmentComponent
  ],
  templateUrl: './projects-finance-instalment-form.component.html',
  styleUrl: './projects-finance-instalment-form.component.scss'
})
export class ProjectsFinanceInstalmentFormComponent implements OnInit {
  @Input() projectId!: number;
  @Input() projectInvoiceId: number | null = null;

  invoice: ProjectInvoice | null = null;
  selectedFile: File | null = null;
  isLoading = false;
  isSaving = false;

  readonly currency = PROJECT_FINANCE_DEFAULT_CURRENCY;
  readonly statusLabel = projectInvoiceStatusLabel;
  readonly statusClass = projectInvoiceStatusClass;

  form = new FormGroup({
    dueDate: new FormControl<string | null>(null, Validators.required),
    paidDate: new FormControl<string | null>(null),
    bankReceived: new FormControl<string | null>(null),
    reference: new FormControl<string | null>(null),
    note: new FormControl<string | null>(null)
  });

  constructor(
    private projectFinanceService: ProjectFinanceService,
    private alertNotificationService: AlertNotificationService,
    private routeService: RouteService
  ) { }

  ngOnInit(): void {
    this.loadInvoice();
  }

  getControl(name: keyof typeof this.form.controls): FormControl {
    return this.form.controls[name] as FormControl;
  }

  readonly validateInvoiceFile = (file: File): string | null => this.projectFinanceService.validateInvoiceFile(file);

  onPendingFileChange(file: File | null): void {
    this.selectedFile = file;
  }

  openAttachment(): void {
    const url = this.invoice?.attachment?.url;
    if (!url) {
      this.alertNotificationService.warning('File URL not available');
      return;
    }
    this.projectFinanceService.attachmentPublicUrl(url).subscribe({
      next: (publicUrl: string) => {
        if (!publicUrl) {
          this.alertNotificationService.error('Error downloading file');
          return;
        }
        window.open(publicUrl, '_blank');
      },
      error: (error: any) => {
        console.error('Error downloading the invoice:', error);
        this.alertNotificationService.error('Error downloading file');
      }
    });
  }

  cancel(): void {
    this.routeService.projectsFinanceIncome(this.projectId);
  }

  save(): void {
    if (!this.invoice || this.projectInvoiceId === null) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertNotificationService.warning('Please fill in all required fields');
      return;
    }

    const value = this.form.getRawValue();
    const payload: ProjectInvoiceUpdatePayload = {
      dueDate: value.dueDate as string,
      paidDate: value.paidDate || null,
      bankReceived: value.bankReceived?.trim() || null,
      reference: value.reference?.trim() || null,
      note: value.note?.trim() || null
    };
    const projectInvoiceId = this.projectInvoiceId;
    const file = this.selectedFile;

    this.isSaving = true;
    this.projectFinanceService.updateInvoice(this.projectId, projectInvoiceId, payload).pipe(
      concatMap(response => file
        ? this.projectFinanceService.uploadInvoiceAttachment(this.projectId, projectInvoiceId, file)
        : of(response))
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.alertNotificationService.success('Instalment updated successfully');
        this.routeService.projectsFinanceIncome(this.projectId);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error updating instalment:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error updating instalment'));
      }
    });
  }

  private loadInvoice(): void {
    if (this.projectInvoiceId === null) return;
    const projectInvoiceId = this.projectInvoiceId;

    this.isLoading = true;
    this.projectFinanceService.listInvoices(this.projectId).subscribe({
      next: (response) => {
        this.isLoading = false;
        const invoices = this.projectFinanceService.readResponseKey<ProjectInvoice[]>(response, 'projectInvoices') ?? [];
        this.invoice = invoices.find(invoice => invoice.projectInvoiceId === projectInvoiceId) ?? null;
        if (!this.invoice) {
          this.alertNotificationService.error('Instalment not found');
          return;
        }
        this.form.patchValue({
          dueDate: this.invoice.dueDate,
          paidDate: this.invoice.paidDate,
          bankReceived: this.invoice.bankReceived,
          reference: this.invoice.reference,
          note: this.invoice.note
        });
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading instalment:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error loading instalment'));
      }
    });
  }
}
